import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { createPage, fetchPageById } from '@/store/pages/thunks';
import { Builder } from '@/pages/editor/Editor';

import {
  selectReadPageLoading,
  selectPageNotFound,
  selectPageInvalid,
} from '@/store/pages/selectors';
import { selectIsFeatureEnabled, selectFeatureFlagsHydrated } from '@/store/featureFlags/selectors';
import { fetchFeatureFlags } from '@/store/featureFlags/thunks';
import { useAppReady } from '@/context/FontProvider';
import Loader from '@/pages/loader/Loader';
import { setActiveGlobalStyle } from '@/store/globalStyles/slice';
import { fetchUser } from '@/store/user/thunks';
import { selectUser, selectUserError, selectUserLoading } from '@/store/user/selectors';
import PageNotFound from '@/pages/404';
import { useHubSpot, HUBSPOT_EVENT } from '@/hooks/useHubSpot';
import { runStyleJsonMigrations } from '@builder4/shared/migrations/style/runner';
import { captureSentryException } from '@/utils/sentry';
import { validateStylesConfig } from '@builder4/shared/dto/styles/validateStylesConfig';
import { AuthGuard } from '@/components/auth';
import { FullScreenErrorFallback } from '@/components/ErrorBoundary';
import { useMixpanel } from '@/hooks/useMixpanel';
import { NEW_PAGE_CREATED } from '@/analytics/mixpanel/constants/events';

const PageCreationScreen = lazy(() => import('@/pages/pageCreation/PageCreationScreen'));

const BuilderPageHandler: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasCreated = useRef(false);
  const [error, setError] = React.useState<Error | null>(null);
  const track = useMixpanel();

  useEffect(() => {
    if (hasCreated.current) return; // prevent double run in StrictMode
    hasCreated.current = true;

    track(NEW_PAGE_CREATED);

    const createAndRedirect = async () => {
      try {
        const result = await dispatch(createPage()).unwrap();
        navigate(`/page/${result.id}`);
      } catch (error) {
        console.error('Failed to create page:', error);
        setError(error as Error);
      }
    };

    createAndRedirect();
  }, [dispatch, navigate, track]);

  if (error) {
    return <FullScreenErrorFallback />;
  }

  return <Loader />;
};

const BuilderPageWithId: React.FC = () => {
  const pageLoading = useAppSelector(selectReadPageLoading);
  const user = useAppSelector(selectUser);
  const userLoading = useAppSelector(selectUserLoading);
  const userError = useAppSelector(selectUserError);
  const pageNotFound = useAppSelector(selectPageNotFound);
  const pageInvalid = useAppSelector(selectPageInvalid);
  const { isReady: fontsReady } = useAppReady();
  const { uuid } = useParams<{ uuid: string }>();
  const dispatch = useAppDispatch();
  const trackHubSpot = useHubSpot();
  const hasTrackedPageLoad = useRef(false);

  useEffect(() => {
    const fetchPageAndGlobalStyle = async () => {
      if (!uuid) return;
      try {
        const result = await dispatch(fetchPageById(uuid)).unwrap();
        // Set global style if available
        if (result?.globalStyle?.id) {
          const migrationResult = runStyleJsonMigrations(result.globalStyle.globalStyle);
          if (migrationResult.error) {
            const error = new Error(`Migration error: ${migrationResult.errorMessage}`);
            captureSentryException(error, {
              tags: { feature: 'global-style-loading', operation: 'migration' },
            });
            console.error('Migration error: ', migrationResult.errorMessage);
            throw error;
          }
          const migratedGlobalStyle = migrationResult.json;
          const validationResult = validateStylesConfig(migratedGlobalStyle);
          if (!validationResult.isValid) {
            const error = new Error('Invalid global style');
            captureSentryException(error, {
              tags: { feature: 'global-style-loading', operation: 'validation' },
              extra: {
                globalStyleId: result.globalStyle.id,
                validationErrors: validationResult.errorMessages,
              },
            });
            console.error('Invalid global style: ', validationResult.errorMessages);
            throw error;
          }
          dispatch(
            setActiveGlobalStyle({
              ...result.globalStyle,
              globalStyle: migratedGlobalStyle,
            }),
          );
        }
      } catch (error) {
        console.error('Error fetching page:', error);
      }
    };

    fetchPageAndGlobalStyle();
  }, [uuid, dispatch]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        await dispatch(fetchUser()).unwrap();
      } catch (error) {
        // Don't throw - let the error state be handled below
        console.error('Failed to fetch user:', error);
      }
    };
    loadUser();
  }, [dispatch]);

  useEffect(() => {
    const isReady =
      !pageLoading &&
      fontsReady &&
      !userLoading &&
      user?.email &&
      !pageNotFound &&
      !pageInvalid &&
      !userError;

    if (isReady && !hasTrackedPageLoad.current) {
      hasTrackedPageLoad.current = true;
      trackHubSpot(HUBSPOT_EVENT.LOADED_PAGE_STUDIO);
    }
  }, [
    pageLoading,
    fontsReady,
    userLoading,
    user,
    pageNotFound,
    pageInvalid,
    userError,
    trackHubSpot,
  ]);

  if (userError) {
    return <FullScreenErrorFallback />;
  }

  // Show PageNotFound when page doesn't exist
  if (pageNotFound) {
    return <PageNotFound onRefresh={() => window.location.reload()} />;
  }

  // Show loader until both page data, user data, and fonts are ready
  const isLoading = pageLoading || !fontsReady || userLoading;

  // Show disabled Builder with empty canvas when page data is invalid
  if (pageInvalid) {
    return <Builder disabled />;
  }

  return isLoading ? <Loader /> : <Builder />;
};

const PageEntryPoint: React.FC = () => {
  const dispatch = useAppDispatch();
  const hasFeatureFlags = useAppSelector(selectFeatureFlagsHydrated);
  const isAiPageGenerationEnabled = useAppSelector(
    selectIsFeatureEnabled('builder4-page-generation'),
  );

  useEffect(() => {
    void dispatch(fetchFeatureFlags());
  }, [dispatch]);

  // Need to load feature flags before we tell what experience to render
  if (!hasFeatureFlags) {
    return <Loader />;
  }

  // Trigger automatic default page creation and redirect
  if (!isAiPageGenerationEnabled) {
    return <BuilderPageHandler />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <PageCreationScreen />
    </Suspense>
  );
};

export const BuilderRouter: React.FC = () => {
  return (
    <AuthGuard>
      <Routes>
        <Route path="/" element={<Navigate to="/page" replace />} />

        <Route path="/page" element={<PageEntryPoint />} />

        <Route path="/page/:uuid" element={<BuilderPageWithId />} />

        <Route path="*" element={<Navigate to="/page" replace />} />
      </Routes>
    </AuthGuard>
  );
};
